import { useState } from 'react';
import type { Generator } from '../../hooks/useGenerators';
import type { Organization } from '../../hooks/useCurrentOrg';
import type { GeneratorConfigV1 } from '../../types/generator-config';
import { buildPublicGeneratorUrl, copyToClipboard } from '../../lib/publicUrl';

interface PublishPanelProps {
    generator: Generator;
    org: Organization;
    config: GeneratorConfigV1;
    isDirty: boolean;
    publishing: boolean;
    saving: boolean;
    onPublishToggle: (next: boolean) => void;
}

/**
 * Panneau "Publication" : toggle on/off + URL publique + copie.
 * Affiche aussi un warning si la config est trop vide pour être publiée.
 */
export function PublishPanel({
    generator,
    org,
    config,
    isDirty,
    publishing,
    saving,
    onPublishToggle,
}: PublishPanelProps) {
    const [copied, setCopied] = useState(false);

    const url = buildPublicGeneratorUrl(org.slug, generator.slug);
    const hasContent =
        config.steps.length > 0 &&
        config.steps.some((s) => s.fields.length > 0);

    const disabled = publishing || saving;

    const handleToggle = () => {
        if (disabled) return;
        // Si on veut publier mais que c'est vide → on refuse
        if (!generator.is_published && !hasContent) return;
        onPublishToggle(!generator.is_published);
    };

    const handleCopy = async () => {
        const ok = await copyToClipboard(url);
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <section className="pub-panel">
            <header className="pub-head">
                <div className="pub-title-wrap">
                    <span
                        className={`pub-dot${generator.is_published ? ' is-on' : ''}`}
                        aria-hidden
                    />
                    <div>
                        <h3 className="pub-title">Publication</h3>
                        <p className="pub-sub">
                            {generator.is_published
                                ? 'Votre générateur est accessible via le lien ci-dessous.'
                                : 'Votre générateur est en brouillon, visible uniquement par vous.'}
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className={`pub-switch${generator.is_published ? ' is-on' : ''}`}
                    role="switch"
                    aria-checked={generator.is_published}
                    aria-label={generator.is_published ? 'Dépublier' : 'Publier'}
                    onClick={handleToggle}
                    disabled={disabled || (!generator.is_published && !hasContent)}
                >
                    <span className="pub-switch-thumb" aria-hidden />
                </button>
            </header>

            {!hasContent && !generator.is_published && (
                <div className="pub-warn">
                    Ajoutez au moins une étape et un champ avant de publier.
                </div>
            )}

            {generator.is_published && isDirty && (
                <div className="pub-warn pub-warn-info">
                    Vos dernières modifications ne sont pas enregistrées. Utilisez « Enregistrer » pour les rendre visibles publiquement.
                </div>
            )}

            <div className="pub-url-row">
                <div className="pub-url">
                    <span className="pub-url-label">Lien public</span>
                    <span className="pub-url-value" title={url}>{url}</span>
                </div>
                <div className="pub-url-actions">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={handleCopy}
                        disabled={!generator.is_published && !hasContent}
                        title={generator.is_published ? 'Copier le lien' : 'Publiez d\'abord pour partager le lien'}
                    >
                        {copied ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Copié
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                                Copier
                            </>
                        )}
                    </button>
                    {generator.is_published && (
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost"
                            title="Ouvrir dans un nouvel onglet"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Ouvrir
                        </a>
                    )}
                </div>
            </div>
        </section>
    );
}
